import React from 'react'
import styles from './postManageDefaultView.module.scss'
import { useState } from 'react'
import Pagination from '../../components/Pagination/Pagination'
import { useEffect } from 'react'
import axios from 'axios'
import { PencilIcon } from '@heroicons/react/24/solid'
import { useNavigate } from 'react-router-dom'
import PostItem from '../../components/postItem/postItem'
import { getAccessTokenFromStorage } from '../../utils'

const PostManageDefaultView = () => {
	const [tab, setTab] = useState('진행중')
	const [currentPage, setCurrentPage] = useState(1)
	const [myPostList, setMyPostList] = useState([])
	const [activeMyPostList, setActiveMyPostList] = useState([])
	const [closedMyPostList, setClosedMyPostList] = useState([])
	const [loading, setLoading] = useState(false)
	const [dataLoaded, setDataLoaded] = useState(false)
	const [error, setError] = useState(null)
	const navigate = useNavigate()
	const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT

	const fetchMyPostList = async () => {
		try {
			setLoading(true)
			setError(null)

			const accessToken = getAccessTokenFromStorage()

			if (!accessToken) {
				setError('로그인이 필요합니다.')
				return
			}

			const response = await axios.get(`${API_ENDPOINT}/job-post/user/me`, {
				headers: {
					Authorization: 'Bearer ' + accessToken,
				},
				timeout: 10000, // 10초 타임아웃 설정
			})

			setMyPostList(response.data)
			setDataLoaded(true)
		} catch (err) {
			if (err.code === 'NETWORK_ERROR' || err.message === 'Network Error') {
				setError('네트워크 연결을 확인해주세요.')
			} else if (err.response) {
				// 서버에서 응답을 받았지만 에러 상태인 경우
				if (err.response.status === 401) {
					setError('인증이 만료되었습니다. 다시 로그인해주세요.')
				} else {
					setError(`서버 오류: ${err.response.status}`)
				}
			} else if (err.request) {
				// 요청은 보냈지만 응답을 받지 못한 경우
				setError('서버에 연결할 수 없습니다.')
			} else {
				setError('알 수 없는 오류가 발생했습니다.')
			}

			setMyPostList([])
			setDataLoaded(true)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchMyPostList()
	}, [])

	useEffect(() => {
		const fetchActiveMyPostList = () => {
			const activeMyPostList = myPostList.filter((post) => post.status === 'processing')
			setActiveMyPostList(activeMyPostList)
		}
		const fetchClosedMyPostList = () => {
			const closedMyPostList = myPostList.filter((post) => post.status === 'closed')
			setClosedMyPostList(closedMyPostList)
		}
		fetchActiveMyPostList()
		fetchClosedMyPostList()
	}, [myPostList])

	// 페이지네이션 관련 계산
	const itemsPerPage = 5 // 페이지당 아이템 수

	// 현재 탭에 따른 데이터 선택
	const currentTabData = tab === '진행중' ? activeMyPostList : closedMyPostList
	const totalItems = currentTabData.length
	const totalPages = Math.ceil(totalItems / itemsPerPage)

	// 현재 페이지의 데이터 계산
	const startIndex = (currentPage - 1) * itemsPerPage
	const endIndex = startIndex + itemsPerPage
	const currentPageData = currentTabData.slice(startIndex, endIndex)

	const handlePageChange = (page) => {
		setCurrentPage(page)
	}

	const handleClosePost = () => {
		fetchMyPostList()
	}

	return (
		<div className={styles.postManageDefaultView}>
			<header className={styles.hero}>
				<span className={styles.postManageTitle}>공고 관리</span>
				<p className={styles.postManageDesc}>
					상세보기로 공고 페이지로 이동하고, 하단에서 지원자 목록 확인·마감 처리를 할 수
					있습니다.
				</p>
			</header>
			<div className={styles.tabContainer}>
				<div
					className={`${styles.tabItem} ${tab === '진행중' ? styles.activeTab : ''}`}
					onClick={() => {
						setTab('진행중')
						setCurrentPage(1) // 탭 변경 시 첫 페이지로 리셋
					}}>
					진행중 {activeMyPostList.length}
				</div>
				<div
					className={`${styles.tabItem} ${tab === '마감' ? styles.activeTab : ''}`}
					onClick={() => {
						setTab('마감')
						setCurrentPage(1) // 탭 변경 시 첫 페이지로 리셋
					}}>
					마감 {closedMyPostList.length}
				</div>
			</div>
			<div className={styles.postManageList}>
				{loading ? <div className={styles.loadingBox}>데이터를 불러오는 중…</div> : null}
				{error ? <div className={styles.errorBox}>{error}</div> : null}

				{!loading && !error && currentPageData && currentPageData.length > 0 && (
					<div className={styles.postItemContainer}>
						{currentPageData.map((post) => (
							<PostItem
								key={post.id}
								data={post}
								status={tab === '마감' ? 'closed' : 'active'}
								onClose={handleClosePost}
							/>
						))}
					</div>
				)}
				{!loading &&
					!error &&
					dataLoaded &&
					tab === '진행중' &&
					currentTabData &&
					currentTabData.length === 0 && (
						<div className={styles.noActivePostWrapper}>
							<span className={styles.noPostText}>진행중인 공고가 없어요.</span>
							<span className={styles.noPostText}>
								지금 바로 인재를 찾는 공고를 작성해보세요!
							</span>
							<div
								className={styles.jobPostButton}
								onClick={() => navigate('/posts/create/type')}>
								<span className={styles.jobPostButtonText}>
									여러 캐디 지원자를 받아보고 싶어요
								</span>
								<div className={styles.jobPostButtonSecond}>
									<PencilIcon className={styles.jobPostButtonSecondIcon} />
									<span className={styles.jobPostButtonSecondText}>
										공고작성 바로가기
									</span>
								</div>
							</div>
						</div>
					)}

				{!loading && !error && tab === '마감' && currentTabData.length === 0 && (
					<div className={styles.noClosedPostWrapper}>
						<span className={styles.noClosedPostText}>마감된 공고가 아직 없어요.</span>
					</div>
				)}

				{/* 페이지네이션 - 6개 이상일 때만 표시 */}
			</div>
			{totalPages > 1 && (
				<Pagination
					currentPage={currentPage}
					totalPages={totalPages}
					onPageChange={handlePageChange}
					itemsPerPage={itemsPerPage}
				/>
			)}
		</div>
	)
}

export default PostManageDefaultView
